import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { ErrorBoundary } from '@openx/components-core/lib/ErrorBoundary/ErrorBoundary.tsx';
import { LazyLoadable } from '@openx/components-core/lib/LazyLoadable/LazyLoadable.tsx';
import { SnackbarProvider, snakbarStyles } from '@openx/components-core/lib/Snackbar/SnackbarProvider.tsx';
import { theme } from '@openx/components-core/lib/theme/theme.ts';
import { createQueryClient } from '@openx/constants/react-query.ts';
import { globalStyles } from '@openx/constants/styles.ts';
import { useRollbar } from '@openx/utils/hooks/rollbarIntegration/useRollbar.ts';
import { QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Route, Routes } from 'react-router';
import { http } from './api/index.ts';
import { LoginRoute } from './components/LoginRoute.tsx';
import { DISPATCHER_VERSION } from './config.ts';

const DispatcherRedirect = LazyLoadable(() => import('./components/DispatcherRedirect.tsx'), 'DispatcherRedirect');
const Login = LazyLoadable(() => import('./modules/login/Login.tsx'), 'Login');
const ResponseOIDC = LazyLoadable(() => import('./modules/responseOIDC/ResponseOIDC.tsx'), 'ResponseOIDC');
const SsoRedirect = LazyLoadable(() => import('@openx/components-core/lib/SsoRedirect/SsoRedirect.tsx'), 'SsoRedirect');
const AuthProviderBase = LazyLoadable(
  () => import('@openx/components-core/lib/AuthProviderBase/AuthProviderBase.tsx'),
  'AuthProviderBase'
);
const PageNotFound = LazyLoadable(
  () => import('@openx/components-core/lib/PageNotFound/PageNotFound.tsx'),
  'PageNotFound'
);
const DispatcherAccessForbiddenPage = LazyLoadable(
  () => import('./components/DispatcherAccessForbiddenPage.tsx'),
  'DispatcherAccessForbiddenPage'
);
const OIDCAuthProvider = LazyLoadable(
  () => import('@openx/components-core/lib/OIDCAuthProvider/OIDCAuthProvider.tsx'),
  'OIDCAuthProvider'
);

export function App() {
  const { initialize, rollbar } = useRollbar();
  const queryClient = useRef(createQueryClient(http));

  useEffect(() => {
    // biome-ignore lint/suspicious/noConsole: logging info in console
    console.info(`%cDispatcher version: ${DISPATCHER_VERSION}`, 'font-weight:bold;');
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  return (
    <QueryClientProvider client={queryClient.current}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <SnackbarProvider>
            <GlobalStyles styles={[globalStyles, snakbarStyles(theme)]} />
            <CssBaseline />
            <ErrorBoundary rollbar={rollbar}>
              <Routes>
                <Route
                  element={
                    <OIDCAuthProvider>
                      <AuthProviderBase>
                        <DispatcherRedirect />
                      </AuthProviderBase>
                    </OIDCAuthProvider>
                  }
                  path="/"
                />
                <Route element={<ResponseOIDC />} path="/response-oidc" />
                <Route
                  element={
                    <OIDCAuthProvider>
                      <LoginRoute
                        login={<Login />}
                        redirect={
                          <AuthProviderBase>
                            <DispatcherRedirect />
                          </AuthProviderBase>
                        }
                      />
                    </OIDCAuthProvider>
                  }
                  path="/login"
                />
                <Route element={<SsoRedirect />} path="/sso-redirect" />
                <Route element={<DispatcherAccessForbiddenPage />} path="/forbidden" />
                <Route
                  element={
                    <OIDCAuthProvider>
                      <PageNotFound withHeader={false} />
                    </OIDCAuthProvider>
                  }
                  path="/*"
                />
              </Routes>
            </ErrorBoundary>
          </SnackbarProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </QueryClientProvider>
  );
}
